import type { PlayerOptions, ReportPlayback, VideoPlayback } from "./playback";

export function createBrowserPlayer(
  video: HTMLVideoElement,
  url: string,
  report: ReportPlayback,
  onDecodeError: () => void,
  { loop, onFrame }: PlayerOptions,
): VideoPlayback {
  let disposed = false;
  let frameRequest = 0;
  const presented: VideoFrameRequestCallback = (_now, metadata) => {
    onFrame?.(metadata.mediaTime);
    frameRequest = video.requestVideoFrameCallback(presented);
  };
  const listeners = new Map<string, EventListener>();
  const listen = (event: string, callback: () => void) => {
    const previous = listeners.get(event);
    if (previous) video.removeEventListener(event, previous);
    listeners.set(event, callback);
    video.addEventListener(event, callback);
  };
  const time = () => report({ currentTime: video.currentTime });
  listen("loadedmetadata", () => {
    if (Number.isFinite(video.duration)) report({ duration: video.duration });
  });
  listen("loadeddata", () => report({ hasFrame: true, waiting: false }));
  listen("timeupdate", time);
  listen("waiting", () => report({ waiting: true }));
  listen("playing", () => report({ playing: true, waiting: false, ended: false, hasFrame: true }));
  listen("pause", () => report({ playing: false }));
  listen("seeking", () => report({ seeking: true, ended: false }));
  listen("seeked", () =>
    report({ seeking: false, waiting: false, currentTime: video.currentTime }),
  );
  listen("ended", () => report({ ended: true, playing: false, currentTime: video.duration }));
  listen("volumechange", () => report({ volume: video.volume, muted: video.muted }));
  listen("error", () => {
    if (video.error?.code === 3 || video.error?.code === 4) onDecodeError();
    else
      report({
        error: "The video could not be loaded. Try again.",
        waiting: false,
        playing: false,
      });
  });
  video.loop = loop;
  video.muted = loop;
  video.src = url;
  if (onFrame) frameRequest = video.requestVideoFrameCallback(presented);

  return {
    getCurrentTime: () => video.currentTime,
    prepare() {
      video.preload = "auto";
    },
    async play() {
      if (disposed) return;
      if (video.error) video.load();
      if (video.ended || video.currentTime >= video.duration) video.currentTime = 0;
      report({ playing: true, waiting: video.readyState < 3, ended: false, error: null });
      try {
        await video.play();
      } catch (error) {
        if (disposed) return;
        if (error instanceof DOMException && error.name === "NotAllowedError") {
          report({ playing: false, waiting: false });
        } else if (!(error instanceof DOMException && error.name === "AbortError")) {
          if (video.error?.code === 3 || video.error?.code === 4) return;
          report({
            error: "The video could not be played. Try again.",
            waiting: false,
            playing: false,
          });
        }
      }
    },
    pause() {
      video.pause();
      report({ playing: false });
    },
    seek(seconds) {
      report({ currentTime: seconds, seeking: true, ended: false });
      if (video.readyState >= 1) video.currentTime = seconds;
      else {
        listen("loadedmetadata", () => {
          if (Number.isFinite(video.duration)) report({ duration: video.duration });
          video.currentTime = seconds;
        });
      }
    },
    setVolume(volume) {
      video.volume = volume;
    },
    setMuted(muted) {
      video.muted = muted;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      if (onFrame) video.cancelVideoFrameCallback(frameRequest);
      for (const [event, listener] of listeners) video.removeEventListener(event, listener);
      video.pause();
      video.removeAttribute("src");
      video.load();
    },
  };
}
